import React, { HTMLAttributes } from "react";
import cn from "classnames";
import Button from "./button";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  custom?: boolean;
  children?: React.ReactNode;
  dark?: boolean;
  size?: CardSize;
  header?: CardHeaderProps;
}

export interface CardHeaderProps {
  title: React.ReactNode;
  action?: React.ReactNode;
  team?: string;
}
type CardSize = "xs" | "sm" | "md" | "xl";

type CardVariance = {
  base: string;
  size: {
    [key in CardSize]: string;
  };
};

export const CARD_VARIANCE: CardVariance = {
  base: "rounded-xl flex flex-col overflow-hidden relative",
  size: {
    xs: "p-2",
    sm: "p-4",
    md: "pt-4 px-4 pb-6",
    xl: "p-6",
  },
};

function Card(props: CardProps) {
  const classes: string = cn(
    `${props.dark ? "bg-dark" : "bg-surface shadow-card"} ${
      CARD_VARIANCE.base
    }`,
    !props.custom && CARD_VARIANCE.size[props.size || "md"],
    props.className
  );
  return (
    <div className={classes} id={props.id}>
      {props.header && (
        <>
          {props.header.team && (
            <span className="text-label-sm-medium text-secondary uppercase">
              {props.header.team}
            </span>
          )}
          <div className="z-[1] flex flex-row flex-wrap justify-between items-center gap-x-2 gap-y-2 mb-2">
            {props.header.title && (
              <div
                className={`flex text-title-bold items-center min-[360px]:text-headline-bold whitespace-nowrap gap-2 uppercase ${
                  props.dark ? "text-white" : "text-dark"
                } items-center`}
              >
                <span className="flex flex-row items-center gap-1">
                  {props.header.title}
                </span>
              </div>
            )}
            {props.header.action && (
              <div className="flex justify-end items-center gap-1 shrink">
                {typeof props.header.action === "string" ? (
                  <Button variant="card">{props.header.action}</Button>
                ) : (
                  <>{props.header.action}</>
                )}
              </div>
            )}
          </div>
        </>
      )}
      {props.children}
    </div>
  );
}

interface CardTrimProps {
  fromColor: string;
  toColor: string;
}
function Trim(props: CardTrimProps) {
  return (
    <div
      className="after:-top-[400px] right-0 bottom-0 after:bottom-0 left-0 z-[1] after:z-[2] absolute after:absolute after:bg-moneyline-trim after:bg-cover after:bg-center w-full after:w-full h-2 after:h-[574px] overflow-hidden after:rotate-12"
      style={{
        background: `linear-gradient(293deg, #${props.fromColor} 22.6%, #${props.toColor} 73.6%), #FFFFFF`,
      }}
    ></div>
  );
}

export interface CardGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  header?: CardGroupHeaderProps;
}
interface CardGroupHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  headline?: string | JSX.Element;
  subtitle?: string;
  caption?: string | JSX.Element;
  action?: string | JSX.Element;
}
function Group(props: CardGroupProps) {
  return (
    <div {...props} className={cn(`flex flex-col gap-6`, props.className)}>
      {props.header && (
        <div className="flex flex-wrap justify-between items-center gap-x-2 gap-y-4 mb-4 pt-6">
          <div className="flex flex-col">
            {props.header.subtitle && (
              <span className="mb-1 text-label-lg-bold text-secondary-400 uppercase">
                {props.header.subtitle}
              </span>
            )}
            {props.header.headline && (
              <span className="text-dark text-headline-lg uppercase whitespace-nowrap">
                {props.header.headline}
              </span>
            )}
            {props.header.caption && (
              <span className="pt-6 text-base normal-case">
                {props.header.caption}
              </span>
            )}
          </div>
          <div className="flex justify-start overflow-x-auto overflow-y-hidden">
            {props.header.action && (
              <div className="flex flex-row justify-end items-end gap-4">
                {props.header.action}
              </div>
            )}
          </div>
        </div>
      )}
      {props.children}
    </div>
  );
}

Card.Group = Group;
Card.Trim = Trim;
export default Card;
